import React, { useState, useEffect, useMemo, useCallback, useTransition } from 'react';
import axiosConfig from "../../../api/axiosConfig";
import { debounce } from 'lodash';
import TeacherFinancialModal from './components/TeacherFinancialModal';
import TeacherTable from './components/TeacherTable';
import TeacherFormModal from './components/TeacherFormModal';
import TeacherPastLessonsModal from './components/TeacherPastLessonsModal';
import AdvancedFilterPanel from './components/AdvancedFilterPanel';

// Utility functions
const statusToText = (status) => {
  if (status === 1 || status === '1') return 'Aktif';
  if (status === 2 || status === '2') return 'Beklemede';
  return 'Pasif';
};

const statusToColor = (status) => {
  if (status === 1 || status === '1') return 'bg-green-100 text-green-700';
  if (status === 2 || status === '2') return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const TeacherListPage = () => {
  // State definitions
  const [teachers, setTeachers] = useState([]);
  const [lessonGroups, setLessonGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPending, startTransition] = useTransition();
  
  // Sayfalama state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(50);
  
  // Financial modal states
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [selectedTeacherForFinancial, setSelectedTeacherForFinancial] = useState(null);
  const [teacherFinancials, setTeacherFinancials] = useState({});
  
  // Past lessons states
  const [isPastLessonsModalOpen, setIsPastLessonsModalOpen] = useState(false);
  const [selectedTeacherLessons, setSelectedTeacherLessons] = useState([]);
  const [selectedTeacherInfo, setSelectedTeacherInfo] = useState(null);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [students, setStudents] = useState([]);

  // Form data state
  const [formData, setFormData] = useState({
    adi: '',
    telefon: '',
    teltwo: '',
    e_posta: '',
    adres: '',
    iban: '',
    sistem: '',
    aciklama: '',
    status: '',
    password: '',
    ders: []
  });

  // Akıllı filtreleme için state'ler (öğrenci tablosu gibi)
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(''); // Input için ayrı state
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all, 1, 2, 0
  const [lessonFilter, setLessonFilter] = useState('all'); // all veya ders_id
  
  // Gelişmiş filtreleme için state'ler
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedTeacherFilters, setSelectedTeacherFilters] = useState([]);
  const [selectedStudentFilters, setSelectedStudentFilters] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]); // Tüm öğretmenler (filtreleme için)
  const [allStudents, setAllStudents] = useState([]); // Tüm öğrenciler (filtreleme için)
  
  // localStorage'dan sıralama tercihini al
  const savedSortOption = localStorage.getItem('teacherSortOption') || 'none';
  const [selectedSortOption, setSelectedSortOption] = useState(savedSortOption);
  
  // İlk yükleme için sortConfig'i hesapla
  const getInitialSortConfig = () => {
    switch(savedSortOption) {
      case 'name_asc': return { key: 'adi', direction: 'ASC' };
      case 'name_desc': return { key: 'adi', direction: 'DESC' };
      case 'email_asc': return { key: 'e_posta', direction: 'ASC' };
      case 'email_desc': return { key: 'e_posta', direction: 'DESC' };
      case 'high_earnings': return { key: 'earnings', direction: 'DESC' };
      case 'low_earnings': return { key: 'earnings', direction: 'ASC' };
      case 'recent_created': return { key: 'created_at', direction: 'DESC' };
      case 'old_created': return { key: 'created_at', direction: 'ASC' };
      default: return { key: 'id', direction: 'DESC' };
    }
  };
  
  const [sortConfig, setSortConfig] = useState(getInitialSortConfig());

  const [teacherFinancialDetails, setTeacherFinancialDetails] = useState({});
  
  // Fetch teacher financials
  const fetchTeacherFinancials = async () => {
    try {
      const financials = {};
      const financialDetails = {};
      
      for (const teacher of teachers) {
        try {
          // Yeni endpoint'i kullan (MySQL CURRENT_DATE ile)
          const response = await axiosConfig.get(`/admin/teachers/${teacher.id}/current-month-earnings`);
          const data = response.data;
          financials[teacher.id] = data.netToplam || 0;
          financialDetails[teacher.id] = {
            brutToplam: data.brutToplam || 0,
            toplamAvans: data.toplamAvans || 0,
            netToplam: data.netToplam || 0
          };
        } catch (error) {
          financials[teacher.id] = 0;
          financialDetails[teacher.id] = {
            brutToplam: 0,
            toplamAvans: 0,
            netToplam: 0
          };
        }
      }
      setTeacherFinancials(financials);
      setTeacherFinancialDetails(financialDetails);
    } catch (error) {
      console.error('Finansal veriler alınamadı:', error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, []);
  
  const fetchInitialData = async () => {
    try {
      // Ders gruplarını al
      try {
        const lgRes = await axiosConfig.get('/admin/dersgrubu');
        setLessonGroups(lgRes.data || []);
      } catch (err) {
        console.error('Ders grupları alınamadı:', err);
        setLessonGroups([]);
      }
      
      // Şirketleri al
      try {
        const cpRes = await axiosConfig.get('/admin/sirkets');
        setCompanies(cpRes.data || []);
      } catch (err) {
        console.error('Şirketler alınamadı:', err);
        setCompanies([]);
      }
      
      // Öğrencileri al
      try {
        const studRes = await axiosConfig.get('/admin/ogrencis');
        let studentsData = [];
        if (studRes.data) {
          if (Array.isArray(studRes.data)) {
            studentsData = studRes.data;
          } else if (studRes.data.data && Array.isArray(studRes.data.data)) {
            studentsData = studRes.data.data;
          }
        }
        setStudents(studentsData);
        setAllStudents(studentsData); // Filtreleme için tüm öğrencileri sakla
      } catch (err) {
        console.error('Öğrenciler alınamadı:', err);
        setStudents([]);
        setAllStudents([]);
      }
      
      // Tüm öğretmenleri al (filtreleme için)
      try {
        const teacherRes = await axiosConfig.get('/admin/teachers');
        setAllTeachers(teacherRes.data || []);
      } catch (err) {
        console.error('Öğretmenler alınamadı:', err);
        setAllTeachers([]);
      }
    } catch (error) {
      console.error('Genel hata:', error);
    }
  };
  
  // Öğretmenleri getir (sayfalama ve filtreleme ile)
  const fetchTeachersWithPagination = async () => {
    setLoading(true);
    try {
      // Şu an backend'de sayfalama yok, tüm veriyi çekip frontend'de işleyeceğiz
      const response = await axiosConfig.get('/admin/teachers');
      let teachersData = response.data || [];
      
      // Frontend'de filtreleme
      let filteredData = teachersData.filter(teacher => {
        // Arama filtresi
        const matchesSearch = searchTerm === '' || 
          (teacher.adi && teacher.adi.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (teacher.e_posta && teacher.e_posta.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (teacher.telefon && teacher.telefon.includes(searchTerm)) ||
          (teacher.adres && teacher.adres.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (teacher.iban && teacher.iban.includes(searchTerm));
        
        // Durum filtresi
        const matchesStatus = statusFilter === 'all' || String(teacher.status) === statusFilter;
        
        // Ders filtresi
        let matchesLesson = lessonFilter === 'all';
        if (lessonFilter !== 'all') {
          if (Array.isArray(teacher.ders)) {
            matchesLesson = teacher.ders.includes(Number(lessonFilter)) || 
                          teacher.ders.includes(String(lessonFilter));
          }
        }
        
        // Gelişmiş öğretmen filtresi
        const matchesAdvancedTeacher = selectedTeacherFilters.length === 0 || 
          selectedTeacherFilters.some(filter => filter.id === teacher.id);
        
        return matchesSearch && matchesStatus && matchesLesson && matchesAdvancedTeacher;
      });
      
      // Eğer öğrenci filtresi varsa, öğretmenleri öğrencileriyle eşleştir
      if (selectedStudentFilters.length > 0) {
        // Bu öğrencilerle dersi olan öğretmenleri bul
        // Not: Bu backend'de yapılması gereken bir işlem, şimdilik boş bırakıyoruz
        // Backend'de öğretmen-öğrenci ilişkisi kurulması gerekiyor
      }
      
      // Sıralama
      if (sortConfig.key === 'earnings') {
        // Ücrete göre sıralama için finansal verileri kullan
        await fetchTeacherFinancials();
        filteredData.sort((a, b) => {
          const aEarnings = teacherFinancials[a.id] || 0;
          const bEarnings = teacherFinancials[b.id] || 0;
          return sortConfig.direction === 'ASC' ? aEarnings - bEarnings : bEarnings - aEarnings;
        });
      } else {
        // Diğer alanlara göre sıralama
        filteredData.sort((a, b) => {
          let aVal = a[sortConfig.key];
          let bVal = b[sortConfig.key];
          
          if (aVal === null || aVal === undefined) aVal = '';
          if (bVal === null || bVal === undefined) bVal = '';
          
          if (typeof aVal === 'string') {
            return sortConfig.direction === 'ASC' 
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          } else {
            return sortConfig.direction === 'ASC'
              ? aVal - bVal
              : bVal - aVal;
          }
        });
      }
      
      // Sayfalama (frontend'de)
      const startIndex = (currentPage - 1) * perPage;
      const endIndex = perPage === 99999 ? filteredData.length : startIndex + perPage;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      startTransition(() => {
        setTeachers(paginatedData);
        setTotalCount(filteredData.length);
        setTotalPages(Math.ceil(filteredData.length / (perPage === 99999 ? 1 : perPage)));
        setSelectedIds([]);
      });
    } catch (error) {
      console.error('Öğretmenler alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Öğretmenler değişikliklerini izle
  useEffect(() => {
    fetchTeachersWithPagination();
  }, [currentPage, sortConfig, perPage, searchTerm, statusFilter, lessonFilter, selectedTeacherFilters, selectedStudentFilters]);
  
  // searchTerm dışarıdan değişirse inputValue'yu senkronize et
  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);
  
  // Debounced search handler
  const debouncedSearch = useMemo(
    () => debounce((value) => {
      startTransition(() => {
        setSearchTerm(value);
        setCurrentPage(1);
      });
    }, 300),
    []
  );
  
  // Input değişikliğini handle eden fonksiyon
  const handleSearchInputChange = useCallback((value) => {
    setInputValue(value);
    debouncedSearch(value);
    
    if (value === '') {
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, []);

  // Fetch financials when teachers change
  useEffect(() => {
    if (teachers.length > 0) {
      fetchTeacherFinancials();
    }
  }, [teachers]);

  // Financial modal functions
  const openFinancialModal = (teacher) => {
    setSelectedTeacherForFinancial({ id: teacher.id, name: teacher.adi });
    setIsFinancialModalOpen(true);
  };

  const closeFinancialModal = () => {
    setIsFinancialModalOpen(false);
    setSelectedTeacherForFinancial(null);
  };

  // Fetch teacher lessons
  const fetchTeacherLessons = async (teacherId, teacherName) => {
    setLoadingLessons(true);
    setSelectedTeacherInfo({ id: teacherId, name: teacherName });
    try {
      const response = await axiosConfig.get('/admin/ogretmenders');
      const allLessons = response.data.data || [];
      const teacherLessons = allLessons.filter(lesson => lesson.ogretmen_id == teacherId);
      
      // Öğrenci bilgilerini ekle
      const lessonsWithStudentInfo = teacherLessons.map(lesson => {
        const student = students.find(s => s.id == lesson.ogrenci_id);
        return {
          ...lesson,
          ogrenci_name: student?.name || 'Bilinmeyen Öğrenci',
          parent_phone: student?.parent_phone || '',
          ders_grubu_id: lesson.ders_id
        };
      });
      
      setSelectedTeacherLessons(lessonsWithStudentInfo);
      setIsPastLessonsModalOpen(true);
    } catch (error) {
      console.error('Dersler alınırken hata oluştu:', error);
      alert('Dersler yüklenirken bir hata oluştu!');
    } finally {
      setLoadingLessons(false);
    }
  };
  
  const closePastLessonsModal = () => {
    setIsPastLessonsModalOpen(false);
    setSelectedTeacherLessons([]);
    setSelectedTeacherInfo(null);
  };

  // Öğretmen paneline giriş yapma fonksiyonu
  const handleLoginAsTeacher = async (teacherId) => {
    try {
      const response = await axiosConfig.post(`/admin/teachers/${teacherId}/login-as-teacher`);
      
      if (response.data.token) {
        // Öğretmen token'ını localStorage'a kaydet
        localStorage.setItem('teacherToken', response.data.token);
        localStorage.setItem('teacherInfo', JSON.stringify(response.data.teacher));
        
        // Yeni sekmede öğretmen panelini aç
        window.open('/teacher', '_blank');
      }
    } catch (error) {
      console.error('Öğretmen paneline giriş yapılamadı:', error);
      alert('Öğretmen paneline giriş yapılırken hata oluştu!');
    }
  };


  // Form functions
  const openFormForCreate = () => {
    setCurrentTeacher(null);
    setFormData({
      adi: '',
      telefon: '',
      teltwo: '',
      e_posta: '',
      adres: '',
      iban: '',
      sistem: '',
      aciklama: '',
      status: '',
      password: '',
      ders: []
    });
    setIsFormOpen(true);
  };

  const openFormForEdit = (teacher) => {
    setCurrentTeacher(teacher);

    let statusValue = '';
    if (teacher.status === 1 || teacher.status === '1') statusValue = 'aktif';
    else if (teacher.status === 2 || teacher.status === '2') statusValue = 'beklemede';
    else statusValue = 'pasif';

    let dersIds = [];
    if (Array.isArray(teacher.ders)) {
      if (typeof teacher.ders[0] === 'string') {
        dersIds = teacher.ders
          .map(d => {
            const byId = lessonGroups.find(g => String(g.id) === d);
            if (byId) return byId.id;
            const byName = lessonGroups.find(g => g.ders_adi === d);
            return byName ? byName.id : null;
          })
          .filter(id => id !== null);
      } else {
        dersIds = teacher.ders;
      }
    }

    const companyId =
      companies.find(c => c.id === teacher.sistem || c.sirket_adi === teacher.sistem)?.id || '';

    setFormData({
      adi: teacher.adi || '',
      telefon: teacher.telefon || '',
      teltwo: teacher.teltwo || teacher.telefon2 || '',
      e_posta: teacher.e_posta || '',
      adres: teacher.adres || '',
      iban: teacher.iban || '',
      sistem: companyId,
      aciklama: teacher.aciklama || '',
      status: statusValue,
      password: '',
      ders: dersIds
    });

    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentTeacher(null);
    setFormData({
      adi: '',
      telefon: '',
      teltwo: '',
      e_posta: '',
      adres: '',
      iban: '',
      sistem: '',
      aciklama: '',
      status: '',
      password: '',
      ders: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let statusInt = 0;
    if (formData.status === 'aktif') statusInt = 1;
    else if (formData.status === 'beklemede') statusInt = 2;
    else statusInt = 0;

    const payload = { ...formData, status: statusInt };
    try {
      if (currentTeacher) {
        await axiosConfig.put(`/admin/teachers/${currentTeacher.id}`, payload);
      } else {
        await axiosConfig.post('/admin/teachers', payload);
      }
      // Öğretmenleri yeniden yükle
      fetchTeachersWithPagination();
      closeForm();
    } catch (err) {
      console.error('handleSubmit error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu öğretmeni silmek istediğinize emin misiniz?')) return;
    try {
      await axiosConfig.delete(`/admin/teachers/${id}`);
      // Öğretmenleri yeniden yükle
      fetchTeachersWithPagination();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleToggleStudentStatus = async (teacherId, ogretmendersId) => {
    try {
      // ogretmendersId artık doğru ID (ogretmenders.id)
      const response = await axiosConfig.patch(`/admin/ogretmenders/${ogretmendersId}/toggle-status`);

      if (response.data.success) {
        // Başarılı durum değişikliği - sadece local state güncelle, liste yenileme yapma
        const newStatus = response.data.data.new_status;
        const statusText = newStatus === 1 ? 'aktif' : 'pasif';

        // Local state'i güncelle (optimistic update)
        setTeachers(prevTeachers =>
          prevTeachers.map(teacher => {
            if (teacher.id === teacherId) {
              return {
                ...teacher,
                students: teacher.students.map(student =>
                  student.ogretmenders_id === ogretmendersId
                    ? { ...student, status: newStatus }
                    : student
                )
              };
            }
            return teacher;
          })
        );

        // Kullanıcıya bilgi ver
        alert(`Öğrenci durumu ${statusText} yapıldı.`);
      }
    } catch (err) {
      console.error('Toggle student status error:', err);
      if (err.response?.data?.message) {
        alert(`Hata: ${err.response.data.message}`);
      } else {
        alert('Öğrenci durumu değiştirilirken bir hata oluştu.');
      }
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} öğretmeni silmek istediğinize emin misiniz?`)) return;
    try {
      await Promise.all(selectedIds.map(id => axiosConfig.delete(`/admin/teachers/${id}`)));
      // Öğretmenleri yeniden yükle
      fetchTeachersWithPagination();
    } catch (err) {
      console.error('Batch delete error:', err);
    }
  };

  const handleSelectChange = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(selId => selId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = teachers.map(t => t.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };
  
  // Gelişmiş filtreleme handler
  const handleAdvancedFilterChange = ({ selectedTeachers, selectedStudents }) => {
    setSelectedTeacherFilters(selectedTeachers);
    setSelectedStudentFilters(selectedStudents);
    setCurrentPage(1); // Filtreleme değişince ilk sayfaya dön
  };

  const handleDersCheckboxChange = (dersId) => {
    setFormData(prevData => {
      const { ders } = prevData;
      if (ders.includes(dersId)) {
        return { ...prevData, ders: ders.filter(d => d !== dersId) };
      } else {
        return { ...prevData, ders: [...ders, dersId] };
      }
    });
  };

  // Akıllı sıralama fonksiyonu
  const handleSmartSort = (sortType) => {
    let sortKey = 'id';
    let sortDirection = 'DESC';
    
    switch(sortType) {
      case 'none':
        sortKey = 'id';
        sortDirection = 'DESC';
        break;
      case 'name_asc':
        sortKey = 'adi';
        sortDirection = 'ASC';
        break;
      case 'name_desc':
        sortKey = 'adi';
        sortDirection = 'DESC';
        break;
      case 'email_asc':
        sortKey = 'e_posta';
        sortDirection = 'ASC';
        break;
      case 'email_desc':
        sortKey = 'e_posta';
        sortDirection = 'DESC';
        break;
      case 'high_earnings':
        sortKey = 'earnings';
        sortDirection = 'DESC';
        break;
      case 'low_earnings':
        sortKey = 'earnings';
        sortDirection = 'ASC';
        break;
      case 'recent_created':
        sortKey = 'created_at';
        sortDirection = 'DESC';
        break;
      case 'old_created':
        sortKey = 'created_at';
        sortDirection = 'ASC';
        break;
    }
    
    setSelectedSortOption(sortType);
    localStorage.setItem('teacherSortOption', sortType); // Sıralama tercihini kaydet
    
    startTransition(() => {
      setSortConfig({ key: sortKey, direction: sortDirection });
      setCurrentPage(1); // Sıralama değişince ilk sayfaya dön
    });
  };
  
  // Sayfa değiştirme fonksiyonları
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };
  
  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);
  
  // Sayfa başına kayıt sayısı değiştirme
  const handlePerPageChange = (newPerPage) => {
    const value = parseInt(newPerPage);
    
    // Eğer çok fazla kayıt varsa uyarı göster
    if (value === 99999 && totalCount > 2000) {
      if (!window.confirm(
        `${totalCount} kayıt gösterilecek. Bu işlem biraz zaman alabilir ve tarayıcınızı yavaşlatabilir. Devam etmek istiyor musunuz?`
      )) {
        return;
      }
    }
    
    setPerPage(value);
    setCurrentPage(1);
    // Tümünü göster seçildiğinde seçili öğeler temizlensin
    if (value === 99999) {
      setSelectedIds([]);
    }
  };

  // Smart sort options
  const smartSortOptions = [
    { value: 'none', label: 'Sıralama Yok' },
    { value: 'name_asc', label: 'A-Z İsim' },
    { value: 'name_desc', label: 'Z-A İsim' },
    { value: 'email_asc', label: 'A-Z E-posta' },
    { value: 'email_desc', label: 'Z-A E-posta' },
    { value: 'high_earnings', label: 'En Yüksek Kazanç' },
    { value: 'low_earnings', label: 'En Düşük Kazanç' },
    { value: 'recent_created', label: 'En Yeni Kayıtlar' },
    { value: 'old_created', label: 'En Eski Kayıtlar' }
  ];
  
  const allSelected = teachers.length > 0 && teachers.every(t => selectedIds.includes(t.id));
  
  // Sayfa numaraları dizisi oluştur
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const activeFilterCount =
    (statusFilter !== 'all' ? 1 : 0) +
    (lessonFilter !== 'all' ? 1 : 0) +
    (selectedSortOption !== 'none' ? 1 : 0) +
    selectedTeacherFilters.length +
    selectedStudentFilters.length;

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-5">
      <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Başlık */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              Öğretmenler
            </h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {totalCount}
            </span>
            {statusFilter === '0' && (
              <span className="text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                Pasif
              </span>
            )}
            {isPending && (
              <span className="text-xs text-gray-400">güncelleniyor…</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <button
                className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 px-2.5 py-1.5 rounded-md hover:bg-red-50 transition-colors"
                onClick={handleBatchDelete}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Sil ({selectedIds.length})
              </button>
            )}
            <button
              className="inline-flex items-center gap-1.5 text-sm bg-gray-900 text-white px-3 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
              onClick={openFormForCreate}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Yeni
            </button>
          </div>
        </div>

        {/* Araç çubuğu */}
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2.5 border-b border-gray-200 bg-gray-50/50">
          {/* Arama */}
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <svg className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              placeholder="Ara: ad, telefon, e-posta, adres, IBAN…"
              value={inputValue}
              onChange={(e) => handleSearchInputChange(e.target.value)}
            />
          </div>

          {/* Durum */}
          <select
            className="px-2 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="1">Aktif</option>
            <option value="2">Beklemede</option>
            <option value="0">Pasif</option>
          </select>

          {/* Ders */}
          <select
            className="px-2 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 max-w-[180px]"
            value={lessonFilter}
            onChange={(e) => {
              setLessonFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tüm Dersler</option>
            {lessonGroups.map(lesson => (
              <option key={lesson.id} value={lesson.id}>{lesson.ders_adi}</option>
            ))}
          </select>

          {/* Sıralama */}
          <select
            className="px-2 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
            value={selectedSortOption}
            onChange={(e) => handleSmartSort(e.target.value)}
          >
            {smartSortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Gelişmiş filtre toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border transition-colors ${
              showAdvancedFilters
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Gelişmiş
            {(selectedTeacherFilters.length + selectedStudentFilters.length) > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${showAdvancedFilters ? 'bg-white/20' : 'bg-gray-900 text-white'}`}>
                {selectedTeacherFilters.length + selectedStudentFilters.length}
              </span>
            )}
          </button>

          {/* Filtre temizle */}
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setStatusFilter('all');
                setLessonFilter('all');
                handleSmartSort('none');
                setSelectedTeacherFilters([]);
                setSelectedStudentFilters([]);
                setCurrentPage(1);
              }}
              className="text-xs text-gray-500 hover:text-gray-900 px-2 py-1.5"
            >
              Temizle
            </button>
          )}

          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <span>Göster:</span>
            <select
              className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
              value={perPage}
              onChange={(e) => handlePerPageChange(e.target.value)}
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="99999">Hepsi</option>
            </select>
          </div>
        </div>

        {/* Gelişmiş Filtreleme Paneli */}
        {showAdvancedFilters && (
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-white">
            <AdvancedFilterPanel
              teachers={allTeachers}
              students={allStudents}
              lessonGroups={lessonGroups}
              onFilterChange={handleAdvancedFilterChange}
              totalTeachers={allTeachers.length}
              totalStudents={allStudents.length}
            />
          </div>
        )}

        {/* Table */}
        <div className="w-full">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900"></div>
            </div>
          ) : teachers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm">Kayıt bulunamadı</p>
            </div>
          ) : (
            <TeacherTable
              filteredTeachers={teachers}
              selectedIds={selectedIds}
              handleSelectChange={handleSelectChange}
              handleSelectAll={() => {
                if (allSelected) {
                  setSelectedIds([]);
                } else {
                  setSelectedIds(teachers.map(t => t.id));
                }
              }}
              openFormForEdit={openFormForEdit}
              handleDelete={handleDelete}
              handleToggleStudentStatus={handleToggleStudentStatus}
              openFinancialModal={openFinancialModal}
              fetchTeacherLessons={fetchTeacherLessons}
              handleLoginAsTeacher={handleLoginAsTeacher}
              teacherFinancials={teacherFinancials}
              teacherFinancialDetails={teacherFinancialDetails}
              lessonGroups={lessonGroups}
              statusToText={statusToText}
              statusToColor={statusToColor}
            />
          )}
        </div>

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-2.5 border-t border-gray-200 bg-gray-50/50">
            <p className="text-xs text-gray-500">
              {perPage === 99999 ? (
                <>{totalCount} kayıt</>
              ) : (
                <>
                  {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, totalCount)} / {totalCount}
                </>
              )}
            </p>

            {perPage !== 99999 && (
              <nav className="flex items-center gap-0.5">
                <button
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="İlk"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Önceki"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {getPageNumbers().map((number, index) => {
                  if (number === '...') {
                    return (
                      <span key={index} className="px-2 text-gray-400 text-sm">…</span>
                    );
                  }
                  return (
                    <button
                      key={number}
                      onClick={() => goToPage(number)}
                      className={`min-w-[28px] px-2 py-1 rounded-md text-sm transition-colors ${
                        currentPage === number
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {number}
                    </button>
                  );
                })}

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Sonraki"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Son"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </nav>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      <TeacherFormModal
        isFormOpen={isFormOpen}
        closeForm={closeForm}
        currentTeacher={currentTeacher}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        companies={companies}
        lessonGroups={lessonGroups}
        handleDersCheckboxChange={handleDersCheckboxChange}
      />

      {/* Past Lessons Modal */}
      <TeacherPastLessonsModal
        isPastLessonsModalOpen={isPastLessonsModalOpen}
        closePastLessonsModal={closePastLessonsModal}
        selectedTeacherLessons={selectedTeacherLessons}
        selectedTeacherInfo={selectedTeacherInfo}
        loadingLessons={loadingLessons}
        lessonGroups={lessonGroups}
      />

      {/* Financial Modal */}
      <TeacherFinancialModal 
        isOpen={isFinancialModalOpen}
        onClose={closeFinancialModal}
        teacherId={selectedTeacherForFinancial?.id}
        teacherName={selectedTeacherForFinancial?.name}
      />
    </div>
  );
};

export default TeacherListPage;